/*
 * FavoritePage.jsx
 *
 * Speakers
 * Created by Mahdi Yousefpour on Thu Mar 02 2023 mys Company
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { localization } from '../../../config/localize/localizable'
import { useFavoriteHooks } from '../hooks/useFavoriteHooks'
import FormikTextField from '../../../component/element/FormikTextField'
import DoneButton from '../../../component/element/DoneButton'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-hot-toast'
import { isNullOrEmpty } from '../../../utilities/mysUtilities'
import { useFileHooks } from '../../file/hooks/useFileHooks'
import TableOfAttachments from '../../../component/general/TableOfAttachments'
import SidePanelUploadSingleImage from '../../../component/general/SidePanelUploadSingleImage'
import FileUploaderTopPanelTable from '../../../component/general/FileUploaderTopPanelTable'
import { getPermissions } from '../../../config/constant/constants'
import { MenuName } from '../../../config/menu/MenuName'
import SimplePageTitle from '../../../component/general/SimplePageTitle'
import DefaultEditor from '../../../component/element/DefaultEditor'

const FavoritePage = () => {
    //
    // MARK: VARIABLE

    const params = useParams()
    const navigate = useNavigate()
    const service = useFavoriteHooks()
    const [model, setModel] = useState({})
    const [currentTab, setCurrentTab] = useState(1)
    let tabStyle = 'outline-none nav-link block text-xs border-t-0 border-b-2 border-transparent px-6 py-3 my-2 hover:border-transparent hover:bg-gray-100 focus:border-transparent'

    const [files, setFiles] = useState([])
    const fileService = useFileHooks()

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(localization.MSG_title_isrequired).min(3, localization.MSG_mincharacter).max(500, localization.MSG_maxcharacter),
        description: Yup.string(),

        urlPath: Yup.string(),
        registerDate: Yup.string(),
        metaDescription: Yup.string(),
        metaTitle: Yup.string(),
        primaryImage: Yup.string(),
    })

    const formik = useFormik({
        initialValues: {
            id: model?.id,
            title: model?.title ?? '',
            description: model?.description ?? '',

            urlPath: model?.urlPath ?? '',
            registerDate: model?.registerDate ?? '',
            metaDescription: model?.metaDescription ?? '',
            metaTitle: model?.metaTitle ?? '',
            primaryImage: model?.primaryImage ?? '',
        },
        enableReinitialize: true,
        validationSchema,
        // validateOnChange: false,
        validateOnBlur: true,
        onSubmit: (values) => {
            isNullOrEmpty(values.id) ? save(values) : update(values)
        },
    })

    //
    // MARK: METHODS

    useEffect(() => {
        if (getPermissions().filter((x) => x.menuTitle.toLowerCase() === MenuName.favorite.toLowerCase()).length === 0) navigate('/')
        if (params?.id > 0) {
            getOne()
            getFiles()
        }
    }, [])

    const getOne = async () => {
        await service.getOne(params?.id, {
            onSuccess: (response) => {
                setModel(response.data.result)
                formik.setValues(response.data.result)
            },
        })
    }

    const save = (body) => {
        service.post(body, {
            onSuccess: async (response) => {
                setModel(response.data.result)
                formik.values.id = response.data.result.id
                formik.setValues(formik.values)
                toast.success(response.data.messages[0])
            },
        })
    }

    const update = (model) => {
        service.put(model, {
            onSuccess: async (response) => {
                setModel(response.data.result)
                formik.setValues(formik.values)
                toast.success(response.data.messages[0])
            },
        })
    }

    const getFiles = async () => {
        await fileService.getBySourceId(
            { type: 'favorite', sourceId: params?.id },
            {
                onSuccess: (response) => {
                    setFiles(response.data.results)
                },
            }
        )
    }

    const handleSubmitClick = () => {
        formik.values.urlPath = formik.values.title.replaceAll(' ', '-')

        formik.handleSubmit()
    }

    return (
        <>
            <div className="col-span-3 ">
                <SimplePageTitle title={localization.favorite + (model?.title ? ' - ' + model?.title : '')} />

                {/* <h6 className="text-xs"></h6> */}
                <div className="w-full grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 ">
                    <div className="col-span-4 sm:col-span-2 md:col-span-3 lg:col-span-4 bg-white mt-3 p-2  shadow-md rounded-md">
                        <ul className="nav nav-tabs flex list-none border-b-0 pl-0 mb-4">
                            <li className="nav-item" onClick={(e) => setCurrentTab(1)}>
                                <span className={`${tabStyle} ${currentTab === 1 ? 'active' : ''}`}>{localization.caption_baseinfo}</span>
                            </li>

                            <li className="nav-item" onClick={(e) => setCurrentTab(2)}>
                                <span className={`${tabStyle} ${currentTab === 2 ? 'active' : ''}`}>{localization.meta}</span>
                            </li>

                            <li className="nav-item" onClick={(e) => setCurrentTab(3)}>
                                <span className={`${tabStyle} ${currentTab === 3 ? 'active' : ''}`}>{localization.images}</span>
                            </li>

                            <li className="nav-item" onClick={(e) => setCurrentTab(4)}>
                                <span className={`${tabStyle} ${currentTab === 4 ? 'active' : ''}`}>{localization.videos}</span>
                            </li>

                            <li className="nav-item" onClick={(e) => setCurrentTab(5)}>
                                <span className={`${tabStyle} ${currentTab === 5 ? 'active' : ''}`}>{localization.sounds}</span>
                            </li>
                        </ul>
                        <div>
                            {currentTab === 1 && (
                                <div className="">
                                    {/* <div> Headers</div> */}
                                    <form className="w-full p-2">
                                        <div className="form-group mb-3">
                                            <FormikTextField name="title" formik={formik} label={localization.title} placeHolder={localization.title} />
                                        </div>

                                        <div className="form-group mb-3">
                                            <DefaultEditor name="description" label={localization.description} value={model?.description} formik={formik} />
                                        </div>

                                        <div className="form-group mb-3 mt-4 grid grid-cols-5 " dir="ltr">
                                            <DoneButton type="button" buttonClassName="bg-purple-500" label={localization.ok} onClick={handleSubmitClick} />
                                        </div>
                                    </form>
                                </div>
                            )}
                            {currentTab === 2 && (
                                <div className="">
                                    <div className="form-group mb-3">
                                        <FormikTextField name="urlPath" formik={formik} label={localization.urlPath} placeHolder={localization.urlPath} />
                                    </div>

                                    <div className="form-group mb-3">
                                        <FormikTextField name="metaTitle" formik={formik} label={localization.metaTitle} placeHolder={localization.metaTitle} />
                                    </div>

                                    <div className="form-group mb-3">
                                        <FormikTextField name="metaDescription" formik={formik} label={localization.metaDescription} placeHolder={localization.metaDescription} />
                                    </div>

                                    <div className="form-group mb-3 mt-4 grid grid-cols-5 " dir="ltr">
                                        <DoneButton type="button" buttonClassName="bg-purple-500" label={localization.update} onClick={handleSubmitClick} />
                                    </div>
                                </div>
                            )}

                            {currentTab === 3 && (
                                <div className="">
                                    <FileUploaderTopPanelTable type="favorite" sourceId={params?.id} multiple={true} label={localization.add + ' ' + localization.images} uploaded={() => getFiles()} />
                                    <TableOfAttachments items={files.filter((x) => (x.mimeType ?? '').includes('image'))} />
                                </div>
                            )}

                            {currentTab === 4 && (
                                <div className="">
                                    <FileUploaderTopPanelTable type="favorite" sourceId={params?.id} accepts="video/mp4" label={localization.add + ' ' + localization.video} uploaded={() => getFiles()} />
                                    <TableOfAttachments items={files.filter((x) => (x.mimeType ?? '').includes('video'))} />
                                </div>
                            )}

                            {currentTab === 5 && (
                                <div className="">
                                    <FileUploaderTopPanelTable type="favorite" sourceId={params?.id} accepts="audio/*" label={localization.add + ' ' + localization.sound} uploaded={() => getFiles()} />
                                    <TableOfAttachments items={files.filter((x) => (x.mimeType ?? '').includes('audio'))} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-span-1 p-2">
                        <SidePanelUploadSingleImage
                            title={model?.title}
                            imageId={model?.primaryImage}
                            type="favorite"
                            sourceId={params?.id}
                            uploaded={(id) => {
                                console.log(id)
                                formik.values = { ...formik.values, primaryImage: id }
                                setTimeout(() => {
                                    service.put(formik.values, {
                                        onSuccess: async (response) => {
                                            setModel(response.data.result)
                                            toast.success(response.data.messages[0])
                                        },
                                    })
                                }, 100)
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default FavoritePage
